import React, { useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { InfoCard, Progress } from '@backstage/core-components';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
  Tooltip,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import LockIcon from '@material-ui/icons/Lock';

const useStyles = makeStyles(theme => ({
  formControl: { minWidth: 320, marginBottom: theme.spacing(2) },
  deployButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': { backgroundColor: theme.palette.primary.dark },
  },
  levelBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    marginLeft: 8,
  },
  blockedBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: theme.palette.text.disabled,
    color: '#fff',
    marginLeft: 8,
  },
  statusText: { marginTop: theme.spacing(1) },
}));

interface EnvironmentOption {
  entityRef: string;
  name: string;
  title: string;
  level: number;
  envType: string;
  requiresApproval: boolean;
  deployed: boolean;
  blocked: boolean;
  blockReason?: string;
}

function defaultCatalogName(appName: string, environmentName: string): string {
  return `${appName}_${environmentName}`
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Displays a dropdown of eligible environments to deploy this app to.
 * Enforces strict level ordering: you can only deploy to level N+1
 * where N is the highest currently deployed level.
 */
export function AzureAppDeployCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const meta = entity.metadata as Record<string, any>;
  const appName = entity.metadata.name;
  const organization = meta.adoOrganization as string | undefined;
  const project = meta.adoProject as string | undefined;
  const repoName = meta.adoRepoName as string | undefined;
  const appEntityRef = `component:${entity.metadata.namespace ?? 'default'}/${appName}`;

  // Current deployed environment refs (only azureenvironment: entries)
  const currentDependsOn: string[] = ((entity.spec as any)?.dependsOn ?? []).filter(
    (r: string) => r.toLowerCase().startsWith('azureenvironment:'),
  );
  const envType: string = (meta.envType as string) ?? '';

  const [environments, setEnvironments] = useState<EnvironmentOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRef, setSelectedRef] = useState('');
  const [catalogName, setCatalogName] = useState('');
  const [status, setStatus] = useState<'idle' | 'deploying' | 'done' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');
  const [pipelineUrl, setPipelineUrl] = useState('');
  const [runUrl, setRunUrl] = useState('');

  const waitForRun = async (
    baseUrl: string,
    token: string | undefined,
    runId: number,
  ): Promise<void> => {
    for (let attempt = 0; attempt < 360; attempt++) {
      await new Promise(resolve => setTimeout(resolve, 5000));
      const res = await fetch(
        `${baseUrl}/pipelines/run-status?organization=${encodeURIComponent(organization!)}&project=${encodeURIComponent(project!)}&runId=${runId}`,
        { headers: { Authorization: `Bearer ${token ?? ''}` } },
      );
      if (!res.ok) continue;

      const run = await res.json();
      setStatusMsg(
        `Pipeline ${run.status}${run.result ? ` (${run.result})` : ''}…`,
      );
      if (run.status === 'completed') {
        if (run.result !== 'succeeded') {
          throw new Error(
            `Deployment pipeline completed with result: ${run.result ?? 'unknown'}`,
          );
        }
        return;
      }
    }
    throw new Error('Timed out waiting for the deployment pipeline to complete');
  };

  const updateDeploymentStatus = async (
    baseUrl: string,
    token: string | undefined,
    deployed: boolean,
  ) => {
    const res = await fetch(`${baseUrl}/components/environment-deployment-status`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token ?? ''}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        environmentRef: selectedRef,
        deployed,
        organization,
        project,
        repoName,
      }),
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error ?? res.statusText);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await identityApi.getCredentials();

        // Fetch all AzureEnvironment entities
        const result = await catalogApi.getEntities({
          filter: { kind: 'AzureEnvironment' },
        });

        // Fetch deployed environment entities to find current levels
        const deployedEntities = await Promise.all(
          currentDependsOn.map(ref =>
            catalogApi.getEntityByRef(ref).catch(() => null),
          ),
        );

        const deployedLevels = deployedEntities
          .filter(Boolean)
          .map(e => Number((e!.metadata as any).level ?? 0));

        const maxDeployedLevel = deployedLevels.length > 0 ? Math.max(...deployedLevels) : 0;
        const nextRequiredLevel = maxDeployedLevel + 1;

        // Normalise current dependsOn to lower case for comparison
        const deployedRefSet = new Set(currentDependsOn.map(r => r.toLowerCase()));

        const options: EnvironmentOption[] = result.items
          .filter(e => {
            // Only show matching envType (if the app has a type)
            if (envType) {
              const eType = (e.metadata as any).environmentType as string ?? '';
              if (eType && eType !== envType) return false;
            }
            return true;
          })
          .map(e => {
            const eMeta = e.metadata as Record<string, any>;
            const level = Number(eMeta.level ?? 0);
            const ref = `azureenvironment:${e.metadata.namespace ?? 'default'}/${e.metadata.name}`;
            const deployed = deployedRefSet.has(ref.toLowerCase());
            let blocked = false;
            let blockReason: string | undefined;

            if (deployed) {
              blocked = false;
            } else if (deployedLevels.length === 0) {
              // No deployments yet — only level 1 is allowed
              if (level !== 1) {
                blocked = true;
                blockReason = `Deploy to a level-1 environment first`;
              }
            } else if (level !== nextRequiredLevel) {
              blocked = true;
              blockReason = `Must deploy to level ${nextRequiredLevel} next (current max: ${maxDeployedLevel})`;
            }

            return {
              entityRef: ref,
              name: e.metadata.name,
              title: (eMeta.title as string) ?? e.metadata.name,
              level,
              envType: (eMeta.environmentType as string) ?? '',
              requiresApproval:
                eMeta.deploymentRequiresApproval === true ||
                eMeta.deploymentRequiresApproval === 'true',
              deployed,
              blocked,
              blockReason,
            };
          })
          .sort((a, b) => a.level - b.level);

        if (!cancelled) setEnvironments(options);
      } catch {
        // non-fatal
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeploy = async () => {
    if (!selectedRef || !organization || !project || !repoName) return;
    setStatus('deploying');
    setStatusMsg('');
    setPipelineUrl('');
    setRunUrl('');
    try {
      const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
      const { token } = await identityApi.getCredentials();

      const res = await fetch(`${baseUrl}/components/add-environment`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appEntityRef,
          environmentRef: selectedRef,
          catalogName,
          organization,
          project,
          repoName,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? res.statusText);

      setPipelineUrl(data.pipelineUrl ?? '');
      setRunUrl(data.runUrl ?? '');
      setStatusMsg('Pipeline queued; waiting for it to complete…');
      try {
        await waitForRun(baseUrl, token, data.runId);
      } catch (runError) {
        try {
          await updateDeploymentStatus(baseUrl, token, false);
        } catch {
          // Preserve the pipeline failure as the primary error shown to the user.
        }
        throw runError;
      }
      await updateDeploymentStatus(baseUrl, token, true);
      setStatus('done');
      setStatusMsg(
        data.message
          ? `${data.message} Pipeline completed successfully.`
          : 'Deployment pipeline completed successfully.',
      );
    } catch (e: any) {
      setStatus('error');
      setStatusMsg(e.message ?? 'Unknown error');
    }
  };

  const selectedEnv = environments.find(e => e.entityRef === selectedRef);
  const catalogNameValid = /^[a-z](?:[a-z0-9_]*[a-z0-9])?$/.test(catalogName);
  const canDeploy =
    !!(organization && project && repoName) &&
    !!selectedRef &&
    catalogNameValid &&
    !(selectedEnv?.blocked) &&
    status !== 'deploying' &&
    status !== 'done';

  if (loading) {
    return (
      <InfoCard title="Deploy to Environment">
        <Progress />
      </InfoCard>
    );
  }

  if (!organization || !project || !repoName) {
    return (
      <InfoCard title="Deploy to Environment">
        <Typography variant="body2" color="textSecondary">
          ADO pipeline metadata not found on this entity. Re-scaffold the application to enable
          multi-environment deployment.
        </Typography>
      </InfoCard>
    );
  }

  return (
    <InfoCard title="Deploy to Environment" noPadding={false}>
      {currentDependsOn.length > 0 ? (
        <Typography variant="body2" gutterBottom>
          Currently deployed to <strong>{currentDependsOn.length}</strong> environment(s). Select
          the next environment to promote to:
        </Typography>
      ) : (
        <Typography variant="body2" gutterBottom>
          Select an environment to deploy this application to:
        </Typography>
      )}

      {environments.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No additional environments available.
        </Typography>
      ) : (
        <>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel>Target Environment</InputLabel>
            <Select
              value={selectedRef}
              onChange={e => {
                const nextRef = e.target.value as string;
                const nextEnv = environments.find(env => env.entityRef === nextRef);
                setSelectedRef(nextRef);
                setCatalogName(
                  nextEnv ? defaultCatalogName(appName, nextEnv.name) : '',
                );
              }}
              label="Target Environment"
              disabled={status === 'deploying' || status === 'done'}
            >
              {environments.map(env => (
                <MenuItem key={env.entityRef} value={env.entityRef} disabled={env.blocked}>
                  <span>
                    {env.title}
                    <span className={env.blocked ? classes.blockedBadge : classes.levelBadge}>
                      Level {env.level}
                    </span>
                    {env.deployed && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ display: 'block', fontSize: 11 }}
                      >
                        Already configured — select to redeploy or retry
                      </Typography>
                    )}
                    {env.requiresApproval && (
                      <Tooltip title="Requires approval gate">
                        <LockIcon
                          style={{ fontSize: 14, marginLeft: 6, verticalAlign: 'middle' }}
                        />
                      </Tooltip>
                    )}
                    {env.blocked && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ display: 'block', fontSize: 11 }}
                      >
                        {env.blockReason}
                      </Typography>
                    )}
                  </span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            variant="outlined"
            className={classes.formControl}
            label="Unity Catalog Name"
            value={catalogName}
            onChange={e => setCatalogName(e.target.value.toLowerCase())}
            disabled={status === 'deploying' || status === 'done'}
            error={catalogName.length > 0 && !catalogNameValid}
            helperText={
              catalogName.length > 0 && !catalogNameValid
                ? 'Use lowercase letters, numbers, and underscores; start with a letter.'
                : 'Must be unique across all workspaces attached to the Unity Catalog metastore.'
            }
            required
          />

          <div>
            {selectedEnv && !selectedEnv.blocked && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {selectedEnv.requiresApproval
                  ? 'This environment requires approval before the pipeline can proceed.'
                  : selectedEnv.deployed
                    ? 'This will queue another deployment run for the selected environment.'
                    : 'Deployment will start automatically after the pipeline file is committed.'}
              </Typography>
            )}
            <Button
              variant="contained"
              className={classes.deployButton}
              startIcon={status === 'deploying' ? undefined : <CloudUploadIcon />}
              onClick={handleDeploy}
              disabled={!canDeploy}
            >
              {status === 'deploying'
                ? 'Deploying…'
                : selectedEnv?.deployed
                  ? 'Redeploy'
                  : 'Deploy'}
            </Button>
          </div>
        </>
      )}

      {status === 'deploying' && (
        <div className={classes.statusText}>
          <Progress />
          <Typography variant="body2">
            {statusMsg || 'Adding deployment stage and queueing pipeline run…'}{' '}
            {runUrl && (
              <a href={runUrl} target="_blank" rel="noopener noreferrer">
                View pipeline run →
              </a>
            )}
          </Typography>
        </div>
      )}
      {status === 'done' && (
        <Typography variant="body2" color="primary" className={classes.statusText}>
          {statusMsg}{' '}
          {runUrl ? (
            <a href={runUrl} target="_blank" rel="noopener noreferrer">
              View pipeline run →
            </a>
          ) : pipelineUrl ? (
            <a href={pipelineUrl} target="_blank" rel="noopener noreferrer">
              View pipeline →
            </a>
          ) : null}
          {' '}— Refresh the page to see the updated environment list.
        </Typography>
      )}
      {status === 'error' && (
        <Typography variant="body2" color="error" className={classes.statusText}>
          Error: {statusMsg}{' '}
          {runUrl && (
            <a href={runUrl} target="_blank" rel="noopener noreferrer">
              View pipeline run →
            </a>
          )}
        </Typography>
      )}
    </InfoCard>
  );
}
